import { useNetworkService, type NetworkInterceptor } from "@pasta/box-v2";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

type NetworkRetryOptions = {
  doRequest: (config: AxiosRequestConfig) => Promise<AxiosResponse>;
  delaysMs: number[];
  random: () => number;
};

type RetriedRequestConfig = AxiosRequestConfig & { networkRetryCount?: number };

export class NetworkRetryInterceptor implements NetworkInterceptor {
  public priority = PRIORITY_ABOVE_AUTH;

  constructor(private readonly options: NetworkRetryOptions) {}

  async onError(error: AxiosError): Promise<AxiosResponse> {
    const retryCount = retryCountOf(error.config);
    if (!isRetryable(error) || retryCount >= this.options.delaysMs.length) throw error;

    await sleep(this.withJitter(this.options.delaysMs[retryCount]));
    return this.options.doRequest({ ...error.config, networkRetryCount: retryCount + 1 } as RetriedRequestConfig);
  }

  private withJitter(delayMs: number): number {
    return delayMs * (1 + (this.options.random() - 0.5) * 2 * JITTER_RATIO);
  }
}

// AuthInterceptor is 1 and wraps non-401 errors, so the retry must see them first
const PRIORITY_ABOVE_AUTH = 10;
const JITTER_RATIO = 0.2;
// Covers a back restart (2 to 18 s measured once); total wait about 11 s
const RETRY_DELAYS_MS = [1000, 3000, 7000];

export function createNetworkRetryInterceptor(): NetworkRetryInterceptor {
  return new NetworkRetryInterceptor({
    doRequest: config => useNetworkService().doRequest(config),
    delaysMs: RETRY_DELAYS_MS,
    random: Math.random,
  });
}

export function retryCountOf(config?: AxiosRequestConfig): number {
  return (config as RetriedRequestConfig | undefined)?.networkRetryCount ?? 0;
}

function isRetryable(error: AxiosError): boolean {
  const method = error.config?.method?.toUpperCase();
  return (method === "GET" || method === "HEAD") && !error.response && isConnectionLost(error);
}

function isConnectionLost(error: AxiosError): boolean {
  // axios uses ECONNABORTED for timeouts too: only this message means the browser dropped the request
  const isAborted = error.code === "ECONNABORTED" && error.message === "Request aborted";
  return error.code === "ERR_NETWORK" || isAborted;
}

function sleep(durationMs: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, durationMs));
}
